import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';

function getWebmThumbnail(videoUrl) {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.src = videoUrl;
    video.crossOrigin = "anonymous"; // Important if remote file
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto"; // Ensure data is loaded

    // Add timeout to fail fast on iOS if format is unsupported or loading hangs
    const timeoutId = setTimeout(() => {
      video.remove();
      reject(new Error(`Thumbnail generation timed out for ${videoUrl}`));
    }, 2000);

    video.onloadeddata = () => {
      // Seek to a small offset to ensure we capture a valid frame
      video.currentTime = 0.1;
    };

    video.onseeked = () => {
      clearTimeout(timeoutId);
      try {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const thumbnailUrl = canvas.toDataURL("image/webp");
        resolve(thumbnailUrl);
      } catch (e) {
        reject(e);
      } finally {
        // Clean up
        video.remove();
      }
    };

    video.onerror = (e) => {
      clearTimeout(timeoutId);
      video.remove();
      reject(e);
    };

    // Explicitly trigger load
    video.load();
  });
}

function Instagram() {
  const { t } = useLanguage();
  const instagramUrl = "https://www.instagram.com/mike_j_k_d";
  
  // NOTE: To fetch real posts automatically, you need an Instagram Access Token.
  // You can generate one via the Facebook Developers Portal (Instagram Basic Display API).
  // Once you have it, place it in a .env file as REACT_APP_INSTAGRAM_TOKEN or paste it below.
  const accessToken = process.env.REACT_APP_INSTAGRAM_TOKEN; 

  const [posts, setPosts] = useState([]);

  // Fallback posts to use when API token is missing or fetch fails
  // Define type as 'VIDEO' if you want to show the play button or render a video tag (if media_url is a video file)
  const fallbackConfig = [
    { url: "https://www.instagram.com/p/DL4PkwRtFfo/", type: "IMAGE", media: "1.webp" },
    { url: "https://www.instagram.com/p/DRURK4yDNNL/", type: "VIDEO", media: "2.webm" },
    { url: "https://www.instagram.com/p/DQuIinkjJvU/", type: "VIDEO", media: "3.webm" },
    { url: "https://www.instagram.com/p/DPLZ5yvjJuO/", type: "VIDEO", media: "4.webm" },
    { url: "https://www.instagram.com/p/DL9nUgBtX1v/", type: "VIDEO", media: "5.webm" },
    { url: "https://www.instagram.com/p/DOLSJdQDIaF/", type: "IMAGE", media: "6.webp" },
  ];

  const fallbackPosts = fallbackConfig.map((item, index) => ({
    id: `fallback-${index + 1}`,
    permalink: item.url,
    media_url: `/images/fallback/${item.media}`, 
    thumbnail_url: null, // Will be generated for videos
    media_type: item.type,
    isPlaceholder: true,
  }));

  useEffect(() => {
    const prepareFallbackPosts = async () => {
      const processedPosts = await Promise.all(fallbackPosts.map(async (post) => {
        if (post.media_type === 'VIDEO') {
          try {
            const thumb = await getWebmThumbnail(post.media_url);
            return { ...post, thumbnail_url: thumb };
          } catch (e) {
            console.warn("Failed to generate thumbnail for", post.media_url, e);
            // Fallback: use the same filename but with .webp extension
            const fallbackImage = post.media_url.replace(".webm", ".webp");
            return { ...post, thumbnail_url: fallbackImage };
          }
        }
        return post;
      }));
      setPosts(processedPosts);
    };

    const fetchInstagramPosts = async () => {
      if (!accessToken) {
        await prepareFallbackPosts();
        return;
      }

      try {
        const response = await fetch(
          `https://graph.instagram.com/me/media?fields=id,caption,media_type,media_url,thumbnail_url,permalink&access_token=${accessToken}&limit=6`
        );
        const data = await response.json();
        
        if (data && data.data) {
          setPosts(data.data);
        } else {
          throw new Error("No data received from Instagram");
        }
      } catch (error) {
        console.error("Error fetching Instagram posts:", error);
        await prepareFallbackPosts();
      }
    };

    fetchInstagramPosts();
  }, [accessToken]);

  return (
    <section id="instagram">
      <div className="container">
        
        <div className="instagram-grid">
          {posts.map((post) => {
            const isVideo = post.media_type === 'VIDEO';
            // For VIDEO media_type, the image is in thumbnail_url
            const imageUrl = isVideo ? post.thumbnail_url : post.media_url;
            const postLink = post.permalink || instagramUrl;
            
            // Check if the media_url is actually a video file (for fallback or API)
            const isVideoSource = post.media_url && (post.media_url.endsWith('.mp4') || post.media_url.endsWith('.webm') || post.media_url.includes('video'));

            return (
              <a 
                key={post.id} 
                href={postLink} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="instagram-card"
                onMouseEnter={(e) => {
                  const video = e.currentTarget.querySelector('video');
                  if (video) video.play().catch(() => {});
                }}
                onMouseLeave={(e) => {
                  const video = e.currentTarget.querySelector('video');
                  if (video) {
                    video.pause();
                    video.currentTime = 0;
                  }
                }}
              >
                <div className="instagram-image-container">
                   {isVideoSource ? (
                      <video 
                        src={post.media_url} 
                        poster={imageUrl}
                        muted 
                        loop 
                        playsInline 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                   ) : imageUrl ? (
                       <img src={imageUrl} alt={post.caption || "Instagram post"} />
                   ) : (
                     <div className="placeholder-content">
                        <span>Instagram</span>
                     </div>
                   )}

                   {(isVideo || isVideoSource) && (
                     <div className="play-icon" style={{
                       position: 'absolute',
                       top: '50%',
                       left: '50%',
                       transform: 'translate(-50%, -50%)',
                       width: '40px',
                       height: '40px',
                       backgroundColor: 'rgba(0,0,0,0.6)',
                       borderRadius: '50%',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       pointerEvents: 'none',
                       zIndex: 2,
                       transition: 'opacity 0.3s ease'
                     }}>
                       <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                         <path d="M8 5v14l11-7z" />
                       </svg>
                     </div>
                   )}
                </div>
                <div className="instagram-overlay">
                  <span>View Post</span>
                </div>
              </a>
            );
          })}
        </div>
        
        <div style={{ textAlign: 'center', marginTop: '40px' }}>
          <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="btn">
            {t.instagram.title}
          </a>
        </div>
      </div>

    <div className="trial-cta">
        <h3>{t.faq.cta.title}</h3>
        <p>{t.faq.cta.text}</p>
        <a href="https://docs.google.com/forms/d/1UYNWIYzQmMpG-_qwSAGJJ91QxA8dB4VTOehR0GbS8wQ/viewform " className="btn" target="_blank" rel="noopener noreferrer">{t.faq.cta.button}</a>
      </div>
    </section>
  );
}

export default Instagram;
